import axios from 'axios'

import { validateAttachmentFile } from './attachments'
import type {
  AttachmentProblem,
  ChatAttachment,
  ChatAttachmentApi
} from './attachments'

export type PendingAttachment = {
  localId: string
  file: File
  status: 'uploading' | 'ready' | 'failed'
  progress: number
  serverId?: string
  /** The bytes reached storage, so a retry only has to confirm again. */
  uploaded?: boolean
  attachment?: ChatAttachment
  problem?: AttachmentProblem
  /** Squad read the file and refused it — the same bytes would fail again. */
  rejected?: boolean
  error?: string
  transient?: boolean
}

export type UploadPut = (
  url: string,
  file: File,
  options: {
    contentType: string
    signal: AbortSignal
    onProgress: (percent: number) => void
  }
) => Promise<unknown>

type Failure = Pick<PendingAttachment, 'problem' | 'error' | 'transient'>

// A bare instance: the app interceptors would add `Authorization`, which breaks
// the presigned signature.
const uploadClient = axios.create()

const putWithAxios: UploadPut = (url, file, options) =>
  uploadClient.put(url, file, {
    headers: { 'Content-Type': options.contentType },
    signal: options.signal,
    onUploadProgress: (event) => {
      if (event.total) {
        options.onProgress(Math.round((event.loaded / event.total) * 100))
      }
    }
  })

const serverMessage = (data: unknown) => {
  if (!data || typeof data !== 'object') return null

  const first = Object.values(data as Record<string, unknown>)[0]

  if (typeof first === 'string') return first

  if (Array.isArray(first) && typeof first[0] === 'string') return first[0]

  return null
}

const responseOf = (error: unknown) =>
  (error as { response?: { status?: number; data?: unknown } })?.response

const UPLOAD_CHANGED_CODE = 'upload_changed'

// Module-wide, so local ids stay unique when a workspace switch replaces the
// uploads set while the previous one's message is still in flight.
let counter = 0

const errorCodeOf = (data: unknown) =>
  data && typeof data === 'object' ? (data as { code?: unknown }).code : null

const isUploadChanged = (error: unknown) => {
  const response = responseOf(error)

  return (
    response?.status === 409 &&
    errorCodeOf(response.data) === UPLOAD_CHANGED_CODE
  )
}

const confirmNeedsReupload = (error: unknown) => {
  const response = responseOf(error)

  if (response?.status === 404) return true

  return (
    response?.status === 409 &&
    errorCodeOf(response.data) !== UPLOAD_CHANGED_CODE
  )
}

const failureOf = (error: unknown): Failure => {
  const response = responseOf(error)
  const message = serverMessage(response?.data)
  const refusedAs = (pattern: RegExp) =>
    response?.status === 400 && !!message && pattern.test(message)

  if (response?.status === 413 || refusedAs(/exceeds/i)) {
    return { problem: 'too_large' }
  }

  if (response?.status === 415 || refusedAs(/unsupported/i)) {
    return { problem: 'unsupported' }
  }

  const detail =
    message ?? (error instanceof Error ? error.message : String(error))

  if ((response?.status ?? 500) >= 500 || response?.status === 409) {
    return { transient: true, error: detail }
  }

  return { error: detail }
}

/**
 * The composer's upload state machine (create → presigned PUT → confirm),
 * kept free of React so the abort / discard / retry rules are testable with
 * the repo's plain `node` test runner. `useChatAttachments` subscribes to it.
 */
export const createAttachmentUploads = (
  api: ChatAttachmentApi,
  put: UploadPut = putWithAxios
) => {
  let latest: PendingAttachment[] = []
  const inFlight = new Map<string, AbortController>()
  const listeners = new Set<() => void>()

  const update = (
    next: (current: PendingAttachment[]) => PendingAttachment[]
  ) => {
    latest = next(latest)
    listeners.forEach((listener) => listener())
  }

  const patch = (localId: string, next: Partial<PendingAttachment>) =>
    update((current) =>
      current.map((item) =>
        item.localId === localId ? { ...item, ...next } : item
      )
    )

  const discard = (serverId?: string) => {
    if (serverId) void api.remove(serverId).catch(() => undefined)
  }

  const confirmWithRetry = async (id: string) => {
    try {
      return await api.confirm(id)
    } catch (error) {
      if (!isUploadChanged(error)) throw error

      return api.confirm(id)
    }
  }

  const upload = async (
    localId: string,
    file: File,
    previous?: PendingAttachment
  ) => {
    const controller = new AbortController()
    const resumable = previous?.uploaded ? previous.serverId : undefined
    let serverId = resumable
    let confirming = false
    const abandoned = () =>
      controller.signal.aborted ||
      !latest.some((item) => item.localId === localId)
    const abandon = () => {
      discard(serverId)
      patch(localId, { status: 'failed', serverId: undefined, uploaded: false })
    }

    inFlight.set(localId, controller)
    patch(localId, {
      status: 'uploading',
      progress: resumable ? 100 : 0,
      serverId: resumable,
      uploaded: !!resumable,
      problem: undefined,
      error: undefined
    })

    try {
      if (!serverId) {
        discard(previous?.serverId)

        const target = await api.create(file)

        serverId = target.id

        if (abandoned()) return abandon()

        patch(localId, { serverId })

        await put(target.uploadUrl, file, {
          contentType: target.mimeType,
          signal: controller.signal,
          onProgress: (progress) => patch(localId, { progress })
        })

        if (abandoned()) return abandon()

        patch(localId, { uploaded: true, progress: 100 })
      }

      confirming = true

      const confirmation = await confirmWithRetry(serverId)

      if (abandoned()) return abandon()

      if (confirmation.status === 'ready') {
        patch(localId, {
          status: 'ready',
          progress: 100,
          attachment: confirmation.attachment
        })
      } else {
        patch(localId, {
          status: 'failed',
          rejected: true,
          error: confirmation.reason
        })
      }
    } catch (error) {
      if (abandoned()) return abandon()

      patch(localId, {
        status: 'failed',
        ...failureOf(error),
        ...(confirming && confirmNeedsReupload(error)
          ? { uploaded: false }
          : {})
      })
    } finally {
      if (inFlight.get(localId) === controller) inFlight.delete(localId)
    }
  }

  const add = (files: File[]) => {
    let attached = latest.filter((item) => item.status !== 'failed').length

    const created = files.map((file): PendingAttachment => {
      counter += 1

      const localId = `att-${counter}`
      const problem = validateAttachmentFile(file, attached)

      if (!problem) attached += 1

      return problem
        ? { localId, file, status: 'failed', progress: 0, problem }
        : { localId, file, status: 'uploading', progress: 0 }
    })

    update((current) => [...current, ...created])
    created
      .filter((item) => item.status === 'uploading')
      .forEach((item) => void upload(item.localId, item.file))
  }

  const remove = (localId: string) => {
    const item = latest.find((candidate) => candidate.localId === localId)
    const controller = inFlight.get(localId)

    update((current) =>
      current.filter((candidate) => candidate.localId !== localId)
    )

    if (controller) {
      controller.abort()
    } else {
      discard(item?.serverId)
    }
  }

  const retry = (localId: string) => {
    const item = latest.find((candidate) => candidate.localId === localId)

    if (!item || item.status !== 'failed' || item.problem || item.rejected) {
      return
    }

    void upload(localId, item.file, item)
  }

  /** Files bound to an accepted run belong to that message now: forget them
   * without deleting them on the server. */
  const release = (localIds: string[]) =>
    update((current) =>
      current.filter((item) => !localIds.includes(item.localId))
    )

  /** Leaving the composer: delete what was never sent, abort what is moving
   * (an aborted upload deletes its own row once it settles). */
  const discardUnsent = () => {
    latest
      .filter((item) => !inFlight.has(item.localId))
      .forEach((item) => discard(item.serverId))
    inFlight.forEach((controller) => controller.abort())
  }

  /** "New chat": the same clean-up as leaving, and the chips go too. */
  const discardAll = () => {
    discardUnsent()
    update(() => [])
  }

  return {
    getItems: () => latest,
    subscribe: (listener: () => void) => {
      listeners.add(listener)

      return () => {
        listeners.delete(listener)
      }
    },
    add,
    remove,
    retry,
    release,
    discardUnsent,
    discardAll
  }
}
